import { queryOptions, type QueryClient } from '@tanstack/react-query';
import { AUTH_STATUS_KEY, endSession, type AuthStatus } from '@shared/query/client';
import { ensureAccount } from '@shared/query/account';
import type { AuthGateway, AuthResult } from '@domain/auth/AuthGateway';
import type { EmailLoginCredentials, GoogleCallbackRequest, SocialLoginRequest } from '@domain/auth/auth';

const RETRY_DELAYS_MS = [500, 1500];

export const authStatusQuery = (gateway: AuthGateway) =>
  queryOptions({
    queryKey: AUTH_STATUS_KEY,
    queryFn: async (): Promise<AuthStatus> => {
      const { authenticated } = await gateway.checkAuthStatus();
      if (authenticated) ensureAccount().catch(() => {});
      return { authenticated };
    },
    staleTime: Infinity,
    gcTime: Infinity,
    retry: RETRY_DELAYS_MS.length,
    retryDelay: (attempt) => RETRY_DELAYS_MS[attempt] ?? RETRY_DELAYS_MS[RETRY_DELAYS_MS.length - 1],
  });

const signedIn = (client: QueryClient, result: AuthResult): AuthResult => {
  if (result.authenticated) {
    client.setQueryData<AuthStatus>(AUTH_STATUS_KEY, { authenticated: true });
    ensureAccount().catch(() => {});
  }
  return result;
};

export const authCommands = (gateway: AuthGateway, client: QueryClient) => ({
  async loginWithEmail(credentials: EmailLoginCredentials): Promise<AuthResult> {
    return signedIn(client, await gateway.loginWithEmail(credentials));
  },

  async handleGoogleCallback(request: GoogleCallbackRequest): Promise<AuthResult> {
    return signedIn(client, await gateway.handleGoogleCallback(request));
  },

  initiateSocialLogin(request: SocialLoginRequest) {
    return gateway.initiateSocialLogin(request);
  },

  async logout(): Promise<AuthResult> {
    const result = await gateway.logout();
    endSession();
    return result;
  },
});
